/**
 * Trying, testing and triggering agents from the builder (ADR-0062).
 *
 * - Orchestrator test runs (`POST /v1/test-runs`): a saved version (draft or submitted) runs in
 *   the development environment, actions simulated unless the builder allows real ones.
 * - Evals service (`/api/evals`): test cases per agent and server-side test runs scored with the
 *   same evaluators and gates as `ap-eval`.
 * - Orchestrator triggers: webhook secret (shown once), test events, schedule status.
 */
import type { Http } from "./http";
import type { StartRunResponse } from "./types";

const enc = encodeURIComponent;

// ------------------------------------------------------------------ orchestrator test runs

export interface TestRunRequest {
  /** `name@version` of a saved version (draft or submitted). */
  agent: string;
  input: Record<string, unknown>;
  /** Off by default: every tool call is a dry run and approvals are skipped. */
  allow_real_actions?: boolean;
  priority?: "high" | "normal" | "low";
  /** ADR-0084: this message corrects that test run, still working. */
  correction_of?: string | null;
  /** Keeps a conversation across tries (ADR-0067): earlier turns become the history. */
  conversation_id?: string | null;
  /** Runs inside a case (its documents and history), as the case workspace does. */
  case_id?: string | null;
}

export interface TestRunStarted extends StartRunResponse {
  test: true;
  dry_run: boolean;
}

/** Error codes the orchestrator returns in `detail.code` for test runs and triggers. */
export type TestRunErrorCode = "not_released" | "no_identity" | "not_dev" | "forbidden" | "no_secret" | "token_refresh_needed";

/** `detail.code` of an API error body, if any. */
export function errorCode(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const d = (body as { detail?: unknown }).detail;
  if (d && typeof d === "object" && typeof (d as { code?: unknown }).code === "string") return (d as { code: string }).code;
  return null;
}

/** `detail.reasons` (OPA deny reasons) of an API error body, if any. */
export function errorReasons(body: unknown): string[] {
  if (!body || typeof body !== "object") return [];
  const d = (body as { detail?: unknown }).detail;
  if (d && typeof d === "object" && Array.isArray((d as { reasons?: unknown }).reasons)) return (d as { reasons: unknown[] }).reasons.map(String);
  return [];
}

// ------------------------------------------------------------------ evals service: test cases

export type TestLang = "ar" | "en";

export interface ToolRef {
  tool: string;
  action: string;
}

export interface TestCaseSource {
  kind: "manual" | "case" | "feedback";
  case_id?: string | null;
  run_id?: string | null;
}

export interface TestCaseIn {
  title?: string | null;
  question: string;
  lang: TestLang;
  /** What a good answer looks like (shown next to the agent's answer). */
  expected_answer?: string | null;
  /** Must appear in the answer. */
  key_facts: string[];
  must_not_say: string[];
  /** Tools the agent should use (any order, extra calls allowed). */
  expected_tools: ToolRef[];
  /** Tools the agent must never use. */
  forbidden_tools: ToolRef[];
  should_refuse: boolean | null;
  /** Reference text the answer must be based on (enables the "uses its sources" check). */
  reference_text?: string | null;
  source?: TestCaseSource;
  /** Checks on the answer's fields: conditions on the run's final data (ADR-0071). */
  field_checks?: FieldCheck[];
}

export interface FieldCheck {
  when: string;
  label?: string | null;
}

export interface TestCase extends TestCaseIn {
  id: string;
  agent: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export function emptyTestCase(lang: TestLang = "en"): TestCaseIn {
  return { title: null, question: "", lang, expected_answer: null, key_facts: [], must_not_say: [], expected_tools: [], forbidden_tools: [], should_refuse: null, reference_text: null, source: { kind: "manual" } };
}

// ------------------------------------------------------------------ evals service: test runs

export type GateMetric =
  | "task_success"
  | "groundedness"
  | "safety"
  | "policy_violations"
  | "tool_call_accuracy"
  | "arabic_quality"
  | "latency_p95_ms"
  | "cost_per_case_usd";

export interface GateRow {
  metric: GateMetric | (string & {});
  value: number | null;
  min?: number;
  max?: number;
  status: "pass" | "fail" | "not_measured";
}

export type ReasonCode =
  | "missing_fact"
  | "said_forbidden"
  | "missing_tool"
  | "used_forbidden_tool"
  | "should_refuse"
  | "should_not_refuse"
  | "leaked_private_data"
  | "not_grounded"
  | "run_failed"
  | "waiting_for_person"
  | "timeout"
  | "start_failed";

export interface CaseResult {
  case_id: string;
  title: string | null;
  question: string;
  lang: TestLang;
  expected_answer: string | null;
  key_facts: string[];
  run_id: string | null;
  status: "pending" | "passed" | "failed" | "error";
  answer: string | null;
  reasons: { code: ReasonCode | (string & {}); detail?: string | null }[];
  tool_calls: ToolRef[];
  duration_ms: number | null;
  cost_usd: number | null;
}

export type PostSkippedReason = "draft" | "too_few_cases" | "not_finished" | "post_failed";

export interface TestRunSummary {
  id: string;
  agent: string;
  version: string;
  digest: string;
  status: "running" | "finished" | "failed";
  started_by: string;
  started_at: string;
  finished_at: string | null;
  dry_run: true;
  totals: { cases: number; passed: number; failed: number; errored: number; pending: number };
  passed: boolean | null;
  gates: GateRow[];
  posted: { suite: string; passed: boolean; unblocked_promotions: string[]; at: string } | null;
  post_skipped_reason: PostSkippedReason | null;
}

export interface TestRun extends TestRunSummary {
  results: CaseResult[];
}

/** Gate metrics shown as a percentage (the rest: counts, milliseconds, US dollars). */
export const RATIO_METRICS: readonly string[] = ["task_success", "groundedness", "safety", "tool_call_accuracy", "arabic_quality"];

export function testingApi(http: Http) {
  return {
    /** Orchestrator: run a saved version in the development environment. */
    startTestRun: (req: TestRunRequest) => http.post<TestRunStarted>("orchestrator", "/v1/test-runs", req),
    cases: (agent: string) => http.get<TestCase[]>("evals", `/v1/tests/${enc(agent)}`),
    createCase: (agent: string, body: TestCaseIn) => http.post<TestCase>("evals", `/v1/tests/${enc(agent)}`, body),
    updateCase: (agent: string, id: string, body: TestCaseIn) => http.put<TestCase>("evals", `/v1/tests/${enc(agent)}/${enc(id)}`, body),
    deleteCase: (agent: string, id: string) => http.delete<void>("evals", `/v1/tests/${enc(agent)}/${enc(id)}`),
    runAll: (agent: string, version: string) => http.post<TestRun>("evals", `/v1/tests/${enc(agent)}/runs`, { version }),
    runs: (agent: string, limit = 20) => http.get<TestRunSummary[]>("evals", `/v1/tests/${enc(agent)}/runs`, { limit }),
    run: (agent: string, runId: string) => http.get<TestRun>("evals", `/v1/tests/${enc(agent)}/runs/${enc(runId)}`),
  };
}

// ------------------------------------------------------------------ orchestrator triggers

export interface WebhookStatus {
  agent: string;
  configured: boolean;
  created_at: string | null;
  created_by: string | null;
  url_path: string;
}

export interface WebhookSecret {
  secret: string;
  created_at: string;
  url_path: string;
  rotated: boolean;
  signature_header: string;
  algorithm: string;
}

export interface WebhookTestResult {
  signature_valid: boolean;
  run_id: string | null;
  agent_ref: string;
  test: true;
}

export interface ScheduleStatus {
  id: string;
  cron: string;
  timezone: string;
  next_runs: string[];
  paused: boolean;
  recent: string[];
}

export interface AgentSchedules {
  agent: string;
  environment: string;
  schedules: ScheduleStatus[];
}

export function triggersApi(http: Http) {
  return {
    webhook: (agent: string) => http.get<WebhookStatus>("orchestrator", `/v1/triggers/${enc(agent)}/webhook`),
    /** Creates or rotates the secret; the response is the only time it is shown. */
    createSecret: (agent: string) => http.post<WebhookSecret>("orchestrator", `/v1/triggers/${enc(agent)}/webhook/secret`),
    sendTestEvent: (agent: string, body: { payload?: Record<string, unknown>; version?: string } = {}) =>
      http.post<WebhookTestResult>("orchestrator", `/v1/triggers/${enc(agent)}/webhook/test`, body),
    schedules: (agent: string) => http.get<AgentSchedules>("orchestrator", `/v1/triggers/${enc(agent)}/schedules`),
  };
}

export type TestingApi = ReturnType<typeof testingApi>;
export type TriggersApi = ReturnType<typeof triggersApi>;
